/* ==========================================================================
   Casemira C3 - page collection, vague 5
   1. « Voir 36 tapis de plus » (points 5 de la SPEC, règle 6 du benchmark)
   2. Retour à la même position depuis une fiche (règle 2 du benchmark)
   3. Repêchage d'un rendu AJAX en échec (VAGUE-2 § 6 bis)
   --------------------------------------------------------------------------
   Trois comportements, un seul fichier, parce qu'ils partagent la même
   matière : le bloc `[data-cmlm]` rendu par `snippets/casemira-load-more.liquid`
   au pied de `#ProductGridContainer`, et le fetch d'une page de section.

   Deux principes hérités des vagues 1 et 2, tenus ici aussi :

   · AUCUN compteur n'est calculé en JavaScript. Charger une tranche, c'est
     récupérer le HTML de la page suivante et remplacer le bloc de pied par
     celui que Liquid a rendu pour elle. « 72 sur 490 », le libellé du bouton,
     la progression et l'URL de la tranche d'après en sortent tout faits, avec
     les filtres en cours. Annoncé = obtenu, par construction.

   · TOUS les écouteurs sont délégués au document et posés UNE fois. Le rendu
     AJAX de `facets.js` détruit et recrée `#ProductGridContainer` - donc notre
     bloc - à chaque filtre : rien à rebrancher.

   Sans JavaScript, ce fichier n'existe pas et la page reste juste : la série
   `?page=1…N` est rendue en clair par `snippets/pagination.liquid`, avec ses
   liens `rel="prev"` / `rel="next"`. Elle n'est masquée que si ce script a
   posé `data-cmlm-on` sur `<html>`.
   ========================================================================== */

(function () {
  'use strict';

  /* Le fichier n'est chargé qu'une fois par page (par la section) ; ce garde-fou
     rend une double inclusion inoffensive - la cale de test en pose une, et un
     jour un autre gabarit pourrait le demander aussi. */
  if (window.__cmMoreInit) return;
  window.__cmMoreInit = true;

  var GRID_ID = 'ProductsList';
  var CONTAINER_ID = 'ProductGridContainer';

  /* Mémoire de retour : une entrée par URL de collection (filtres compris),
     dans `sessionStorage` - elle meurt avec l'onglet, ce qui est exactement sa
     durée de vie utile. */
  var STORE_PREFIX = 'casemira:c3-return:';
  var STORE_TTL = 30 * 60 * 1000; /* 30 min : au-delà, la grille a pu bouger  */

  var busy = false;
  var restored = false;

  /* ── Petits accès ─────────────────────────────────────────────────────── */

  function box() {
    return document.querySelector('[data-cmlm]');
  }

  function grid() {
    return document.getElementById(GRID_ID);
  }

  function cardCount() {
    var g = grid();
    return g ? g.querySelectorAll('.f-column').length : 0;
  }

  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* L'identité d'une grille, pour la mémoire de retour : le chemin et les
     paramètres SAUF `page` (une même sélection, à quelque tranche qu'on soit)
     et `section_id` (paramètre technique du rendu AJAX). */
  function stateKey(href) {
    var u = new URL(href || window.location.href, window.location.origin);
    u.searchParams.delete('page');
    u.searchParams.delete('section_id');
    u.hash = '';
    var params = [];
    u.searchParams.forEach(function (value, name) {
      params.push(name + '=' + value);
    });
    params.sort();
    return STORE_PREFIX + u.pathname + '?' + params.join('&');
  }

  function readState(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (!data.ts || Date.now() - data.ts > STORE_TTL) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeState(key, data) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      /* Navigation privée, quota : la restauration se perd, rien d'autre. */
    }
  }

  /* ── URL de tranche ───────────────────────────────────────────────────── */

  /* Celle qu'on affiche dans la barre d'adresse : jamais `section_id`. */
  function publicUrl(raw) {
    var u = new URL(raw, window.location.origin);
    u.searchParams.delete('section_id');
    return u.pathname + (u.search === '?' ? '' : u.search);
  }

  /* Celle qu'on va chercher : le rendu de section seul, ~1 Mo au lieu de la
     page entière. `paginate.next.url` peut déjà porter un `section_id` quand la
     page courante vient elle-même d'un rendu AJAX - on le remplace, on ne
     l'empile pas. */
  function fetchUrl(raw, sectionId) {
    var u = new URL(raw, window.location.origin);
    u.searchParams.delete('section_id');
    if (sectionId) u.searchParams.set('section_id', sectionId);
    return u.toString();
  }

  function sectionId() {
    var b = box();
    if (b && b.getAttribute('data-cmlm-section')) return b.getAttribute('data-cmlm-section');
    var host = document.getElementById(CONTAINER_ID);
    host = host && host.closest('.shopify-section');
    return host ? host.id.replace('shopify-section-', '') : '';
  }

  /* Reconstruit l'URL d'une page donnée à partir de l'URL courante : sert à la
     restauration, qui doit pouvoir demander les pages 1 à N sans avoir gardé
     leurs `paginate.next.url`. */
  function pageUrl(page) {
    var u = new URL(window.location.href, window.location.origin);
    u.searchParams.delete('section_id');
    if (page > 1) {
      u.searchParams.set('page', String(page));
    } else {
      u.searchParams.delete('page');
    }
    return u.pathname + (u.search === '?' ? '' : u.search);
  }

  /* ── Le fetch d'une tranche ───────────────────────────────────────────── */

  function fetchSlice(url) {
    return fetch(fetchUrl(url, sectionId()))
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        if (!doc.getElementById(GRID_ID)) throw new Error('rendu incomplet');
        return doc;
      });
  }

  /* Les cartes d'une tranche, dans l'ordre où Liquid les a rendues. */
  function columnsOf(doc) {
    var list = doc.getElementById(GRID_ID);
    return list ? Array.prototype.slice.call(list.children) : [];
  }

  /* Le pied vient TOUJOURS du HTML de la dernière tranche reçue : c'est lui qui
     porte le compteur cumulé, le libellé du bouton et l'URL d'après. */
  function adoptFooter(doc) {
    var next = doc.querySelector('[data-cmlm]');
    var current = box();
    if (!current) return;

    /* Le bouton est détruit puis recréé : sans cela, le focus du clavier
       retomberait sur `<body>` et il faudrait re-parcourir 72 cartes pour
       revenir au pied de la grille. */
    var hadFocus = current.contains(document.activeElement);

    if (next) {
      current.replaceWith(next);
    } else {
      /* Aucun bloc dans la tranche reçue (collection d'une seule page) :
         on retire le nôtre plutôt que de laisser un bouton sans suite. */
      current.remove();
    }

    if (!hadFocus) return;
    var again = document.querySelector('[data-cmlm-btn]') || document.querySelector('[data-cmlm-count]');
    if (!again) return;
    if (!again.hasAttribute('tabindex') && again.tagName !== 'BUTTON') again.setAttribute('tabindex', '-1');
    again.focus({ preventScroll: true });
  }

  function appendColumns(columns, animate) {
    var g = grid();
    if (!g) return;
    var fade = animate && !reduced();
    columns.forEach(function (column) {
      if (fade) column.classList.add('cmlm-new');
      g.appendChild(column);
    });
    if (!fade) return;
    /* Le fondu de la vague 1 : opacité seule, une seule fois, pas de cascade
       carte par carte (règle 7 du benchmark, et NN/g sur les listes). */
    window.setTimeout(function () {
      columns.forEach(function (column) {
        column.classList.remove('cmlm-new');
      });
    }, 700);
  }

  function loadingState(on) {
    var b = box();
    var button = b && b.querySelector('[data-cmlm-btn]');
    if (!button) return;
    button.classList.toggle('cmlm__btn--busy', !!on);
    button.disabled = !!on;
    button.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  /* ── 1. « Voir N tapis de plus » ──────────────────────────────────────── */

  function loadNext() {
    var b = box();
    if (busy || !b) return Promise.resolve(false);
    var next = b.getAttribute('data-cmlm-next');
    if (!next) return Promise.resolve(false);

    busy = true;
    loadingState(true);

    return fetchSlice(next)
      .then(function (doc) {
        appendColumns(columnsOf(doc), true);
        adoptFooter(doc);
        /* `replaceState` et non `pushState` : le brief le demande, et c'est le
           bon choix - un `pushState` par tranche obligerait à appuyer huit fois
           sur Retour pour quitter une collection parcourue jusqu'au bout. La
           doctrine Google ne demande que « l'URL mise à jour », pas une entrée
           d'historique par tranche. */
        history.replaceState(history.state, '', publicUrl(next));
        busy = false;
        document.dispatchEvent(new CustomEvent('collection:loadedmore'));
        return true;
      })
      .catch(function () {
        busy = false;
        loadingState(false);
        showRetry(null);
        return false;
      });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-cmlm-btn]')) return;
    event.preventDefault();
    loadNext();
  });

  /* ── 2. Le retour à la même position ──────────────────────────────────── */

  function handleOf(node) {
    var link = node && node.closest ? node.closest('a[href*="/products/"]') : null;
    if (!link) return '';
    var path = link.getAttribute('href') || '';
    var match = path.split('?')[0].match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function columnOfHandle(handle) {
    var g = grid();
    if (!g || !handle) return null;
    var link = g.querySelector('a[href*="/products/' + handle + '"]');
    return link ? link.closest('.f-column') : null;
  }

  function snapshot(extra) {
    var b = box();
    var data = {
      ts: Date.now(),
      y: window.scrollY,
      count: cardCount(),
      page: b ? parseInt(b.getAttribute('data-cmlm-page'), 10) || 1 : 1,
      first: firstPage,
      handle: '',
      dy: 0,
    };
    if (extra) {
      data.handle = extra.handle || '';
      data.dy = extra.dy || 0;
    }
    writeState(stateKey(), data);
  }

  /* La page réellement servie au chargement : point de départ de toute
     reconstruction. Sur une collection normale c'est 1 ; sur un lien
     `?page=3` partagé ou suivi par un robot, c'est 3. */
  var firstPage = (function () {
    var value = new URL(window.location.href, window.location.origin).searchParams.get('page');
    var page = parseInt(value, 10);
    return page > 0 ? page : 1;
  })();

  /* On mémorise au CLIC sur une carte (on tient alors le tapis visé et sa
     position exacte à l'écran) et, en secours, à toute sortie de page. */
  document.addEventListener(
    'click',
    function (event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var g = grid();
      var target = event.target;
      if (!g || !target || typeof target.closest !== 'function') return;
      var link = target.closest('a[href*="/products/"]');
      if (!link || !g.contains(link)) return;

      var column = link.closest('.f-column');
      var handle = handleOf(link);
      if (!column || !handle) return;

      /* `dy` est la distance entre le haut de la carte et le haut de l'écran :
         c'est elle, et non `scrollY`, qui rend la page IDENTIQUE au retour,
         quelle que soit la hauteur qu'aura la grille reconstruite. */
      snapshot({ handle: handle, dy: Math.round(column.getBoundingClientRect().top) });
    },
    true
  );

  window.addEventListener('pagehide', function () {
    /* Sans carte cliquée : on garde au moins la tranche et le défilement, pour
       que `scrollRestoration = 'manual'` ne coûte rien à personne. */
    var existing = readState(stateKey());
    if (existing && existing.handle && Math.abs(existing.y - window.scrollY) < 4) return;
    snapshot(null);
  });

  function scrollToState(data) {
    var column = columnOfHandle(data.handle);
    var top;
    if (column) {
      top = column.getBoundingClientRect().top + window.scrollY - (data.dy || 0);
    } else {
      top = data.y || 0;
    }
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    /* Sans animation : la position d'avant n'est pas un endroit où l'on va,
       c'est un endroit d'où l'on n'est jamais parti. */
    window.scrollTo({ top: Math.min(Math.max(0, Math.round(top)), max), behavior: 'auto' });
  }

  /* Recharge en silence les tranches manquantes, puis replace le défilement.
     Trois cas :
       · bfcache - le DOM est intact, il ne reste qu'à replacer ;
       · retour normal sur la même tranche - on ajoute les suivantes ;
       · retour sur une tranche différente (l'URL portait `?page=2`) - on
         reconstruit la série `first…page` en remplaçant la grille. */
  function restore(fromCache) {
    if (restored) return;
    var data = readState(stateKey());
    if (!data) return;

    /* On ne restaure que ce qui EST un retour : traversée d'historique,
       reprise depuis le bfcache, ou arrivée depuis une fiche. Sans ce test,
       une visite neuve sur la même URL, dans le même onglet, se retrouverait
       défilée au milieu de la grille sans l'avoir demandé. */
    var nav = (window.performance.getEntriesByType('navigation') || [])[0];
    var isBack = fromCache || (nav && nav.type === 'back_forward');
    var fromProduct = /\/products\//.test(document.referrer || '');
    if (!isBack && !fromProduct) return;

    restored = true;

    if (cardCount() >= (data.count || 0)) {
      scrollToState(data);
      return;
    }

    var target = Math.max(data.page || 1, firstPage);
    var start = Math.max(1, Math.min(data.first || 1, firstPage));
    var pages = [];
    for (var p = start; p <= target; p += 1) pages.push(p);
    if (pages.length === 0) return;

    var rebuild = firstPage !== start; /* la tranche servie n'ouvre pas la série */
    var chain = Promise.resolve();
    var collected = [];

    pages.forEach(function (page) {
      chain = chain.then(function () {
        if (!rebuild && page === firstPage) return null; /* déjà à l'écran */
        return fetchSlice(pageUrl(page)).then(function (doc) {
          collected.push({ page: page, doc: doc });
          return doc;
        });
      });
    });

    chain
      .then(function () {
        if (collected.length === 0) return;
        var g = grid();
        if (!g) return;
        if (rebuild) g.innerHTML = '';
        collected.forEach(function (slice) {
          appendColumns(columnsOf(slice.doc), false);
        });
        adoptFooter(collected[collected.length - 1].doc);
        history.replaceState(history.state, '', pageUrl(target));
        document.dispatchEvent(new CustomEvent('collection:loadedmore'));
      })
      .then(function () {
        scrollToState(data);
      })
      .catch(function () {
        /* Reconstruction impossible : la grille reste celle que Shopify a
           servie, et on replace au moins le défilement dans ses limites. */
        scrollToState(data);
      });
  }

  /* ── 3. « Réessayer » quand un rendu AJAX échoue ──────────────────────── */

  var retryUrl = null;

  function retryHosts() {
    /* Deux endroits au plus, et toujours ceux où l'œil est déjà posé quand on
       vient de toucher un filtre :

       · le pied de la feuille (téléphone) - masqué à partir de 1000 px, donc
         sans effet sur l'ordinateur ;
       · le pied du panneau ouvert (ordinateur), et seulement à défaut la zone
         de droite de la barre. La barre fait 56 px et sa rangée ne se replie
         pas : y ajouter une phrase quand un panneau, lui, a toute la place,
         serrait le commutateur de densité contre le bord.

       Dans la barre, la phrase disparaît : il ne reste que le bouton. */
    var panel = document.querySelector('.cmc-panel.is-open .cmc-panel__foot');
    return [
      { node: document.querySelector('.cmm-foot'), compact: false },
      panel ? { node: panel, compact: false } : { node: document.querySelector('.cmc__aside'), compact: true },
    ].filter(function (host) {
      return !!host.node;
    });
  }

  function showRetry(url) {
    retryUrl = url || null;
    hideRetry();
    retryHosts().forEach(function (host) {
      var node = document.createElement('div');
      node.className = 'cmc-retry';
      node.setAttribute('role', 'status');
      node.setAttribute('data-cmc-retry-box', '');
      node.innerHTML =
        (host.compact ? '' : '<span class="cmc-retry__t">Liste non mise à jour</span>') +
        '<button type="button" class="cmc-retry__b" data-cmc-retry>Réessayer</button>';
      host.node.appendChild(node);
    });
  }

  function hideRetry() {
    document.querySelectorAll('[data-cmc-retry-box]').forEach(function (node) {
      node.remove();
    });
  }

  document.addEventListener('collection:renderfailed', function (event) {
    showRetry(event.detail && event.detail.url);
  });

  /* Un rendu réussi efface la mention de lui-même : sur ordinateur et sur
     téléphone, `#FacetFiltersContainer` est recréé en entier. La ligne ci-dessous
     couvre le cas où l'échec avait touché un autre pied que celui-là. */
  document.addEventListener('collection:rerendered', hideRetry);

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-cmc-retry]')) return;
    event.preventDefault();

    var form = document.querySelector('form[is="facet-form"]');
    hideRetry();

    if (retryUrl && form && typeof form.renderSection === 'function') {
      /* Le cache de `facets.js` ne garde que les réponses réussies : on
         réémet donc bien une requête, pas une réponse d'erreur mémorisée. */
      form.renderSection(retryUrl, null);
      return;
    }
    if (form && typeof form.onSubmitHandler === 'function') {
      form.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    /* Ni URL ni formulaire : la seule chose honnête qui reste. */
    window.location.reload();
  });

  /* ── Mise en route ────────────────────────────────────────────────────── */

  function activate() {
    if (!box()) return;
    document.documentElement.setAttribute('data-cmlm-on', '');

    /* Seulement sur les collections, et seulement une fois le bloc trouvé :
       c'est nous qui replaçons le défilement au retour, le navigateur ne doit
       pas le faire dans notre dos avec la hauteur d'une grille d'une tranche. */
    if ('scrollRestoration' in window.history) {
      try {
        window.history.scrollRestoration = 'manual';
      } catch (e) {}
    }
  }

  function boot() {
    activate();
    restore(false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', function (event) {
    activate();
    if (event.persisted) {
      /* Reprise depuis le bfcache : le DOM et la position sont là, mais
         `scrollRestoration = 'manual'` a pu empêcher le navigateur de replacer
         la page. On le fait, et une seule fois. */
      restored = false;
      restore(true);
    }
  });

  /* Après un filtre, `#ProductGridContainer` est recréé : le bloc de pied est
     de nouveau celui de la page 1 de la nouvelle sélection, avec son compteur
     « 36 sur 229 » et son bouton. Il n'y a rien à synchroniser - seulement à
     s'assurer que la pagination numérotée reste masquée. */
  document.addEventListener('collection:rerendered', activate);
})();
